import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Alert, InputAdornment, CircularProgress, Chip
} from '@mui/material';
import { LocalOffer, CheckCircle, Cancel } from '@mui/icons-material';
import api from '../../services/api';

/**
 * CouponInput - Reusable coupon code input with validation feedback.
 *
 * Props:
 *   leadId: string         - Lead ID for server-side validation (new registration flow)
 *   planId: string         - Plan ID (upgrade flow — used instead of leadId)
 *   billingCycle: string   - Billing cycle (upgrade flow)
 *   isUpgrade: bool        - If true, uses /subscriptions/apply-coupon instead of /registration/apply-coupon
 *   amount: number         - Current order amount (before discount)
 *   onApplied: (breakdown) => void  - Called when discount is successfully applied
 *   onRemoved: () => void  - Called when coupon is removed
 *   currency: string       - e.g. 'GBP'
 *   currencySymbol: string - e.g. '£'
 */
const CouponInput = ({
  leadId,
  planId,
  billingCycle,
  isUpgrade = false,
  amount,
  onApplied,
  onRemoved,
  currency = 'GBP',
  currencySymbol = '£'
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(null); // { code, discountAmount, finalAmount }
  const [error, setError] = useState('');

  const handleApply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      let res;
      if (isUpgrade) {
        // Upgrade flow: use authenticated subscription context, no leadId needed
        res = await api.post('/subscriptions/apply-coupon', {
          code: code.trim().toUpperCase(),
          planId,
          billingCycle: billingCycle || 'monthly'
        });
      } else {
        // New registration flow: requires leadId
        res = await api.post('/registration/apply-coupon', {
          code: code.trim().toUpperCase(),
          leadId
        });
      }

      if (res.data.success && res.data.valid) {
        const breakdown = res.data.data;
        setApplied(breakdown.couponCode ? { code: breakdown.couponCode, ...breakdown } : null);
        onApplied && onApplied(breakdown);
      } else {
        setError(res.data.message || 'Invalid coupon code');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to validate coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setApplied(null);
    setCode('');
    setError('');
    onRemoved && onRemoved();
  };

  if (applied) {
    return (
      <Box sx={{
        p: 2, borderRadius: 2,
        border: '1.5px solid', borderColor: 'success.main',
        bgcolor: 'rgba(76,175,80,0.06)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle fontSize="small" color="success" />
            <Typography variant="body2" fontWeight={700} color="success.dark">
              Coupon <Chip label={applied.code} size="small" color="success" variant="outlined" sx={{ mx: 0.5 }} /> applied!
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<Cancel fontSize="small" />}
            onClick={handleRemove}
            sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
          >
            Remove
          </Button>
        </Box>
        <Typography variant="body2" color="success.dark" sx={{ mt: 0.5, pl: 3.5 }}>
          You save {currencySymbol}{applied.discountAmount?.toFixed(2)} — New total: <strong>{currencySymbol}{applied.finalAmount?.toFixed(2)}</strong>
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label="Discount Code"
          placeholder="Enter coupon code"
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleApply()}
          size="small"
          fullWidth
          inputProps={{ id: 'coupon-code-input', style: { letterSpacing: '0.05em', fontWeight: 600 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocalOffer fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          error={!!error}
        />
        <Button
          variant="outlined"
          onClick={handleApply}
          disabled={!code.trim() || loading}
          sx={{ minWidth: 90, height: 40, fontWeight: 700 }}
        >
          {loading ? <CircularProgress size={18} /> : 'Apply'}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0.5 }} icon={<Cancel fontSize="small" />}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CouponInput;
